<template>
  <v-container fluid>
    <PageHeader :title="'Accept ' + (isAdditionalInvite ? 'Additional ' : '') + 'Invitation'" icon="mdi-email-open-outline" />
    <v-card class="elevation-1 card mt-4 pa-4 card-form">
      <v-card-text v-if="!invitation" class="text-center pa-8">
        <v-progress-circular indeterminate color="primary" />
      </v-card-text>
      <v-card-text v-if="invitation && currentStep === 1" class="wizard">
        <h4>Terms of Acceptance</h4>
        <p>
          By clicking Next, you agree that you have done any applicable due diligence specific to your company policies
          relevant to accepting documents from outside parties
        </p>
      </v-card-text>
      <v-card-text v-if="invitation && currentStep === 2" class="wizard">
        <v-radio-group v-model="process" @update:modelValue="onChangeProcess">
          <v-radio label="Select a Profile with Existing Connection (Recommended)" value="existing" />
          <v-radio label="Create a New Profile (Advanced)" value="new" />
        </v-radio-group>
      </v-card-text>
      <v-card-text v-if="invitation && currentStep === 3 && process === 'new'" class="wizard">
        <h4>Create New Profile</h4>
        <v-form ref="form" v-model="formValid">
          <v-text-field v-model="companyOrgUnit.name" label="Name" :rules="requiredRule"></v-text-field>
          <v-text-field v-model="companyOrgUnit.code" label="Company Code" :rules="requiredRule"></v-text-field>
          <h2 class="eform-subtitle">Address</h2>
          <v-text-field v-model="companyOrgUnit.addressLineOne" label="Address Line One"></v-text-field>
          <v-select
            v-model="selectedCountry"
            :items="countries"
            label="Country"
            item-title="name"
            item-value="code"
            @update:modelValue="onCountryChange"
          ></v-select>
          <v-select
            v-model="selectedState"
            :items="states"
            label="State"
            item-title="name"
            item-value="code"
          ></v-select>
          <v-text-field v-model="companyOrgUnit.city" label="City"></v-text-field>
          <v-text-field v-model="companyOrgUnit.zipCode" :label="zipCodeLabelName" :rules="zipCodeRules"></v-text-field>
          <h2 class="eform-subtitle">Tax</h2>
          <v-select
            v-model="selectedTaxType"
            :items="taxTypes"
            label="Tax Identification Type"
            item-title="name"
            item-value="code"
          ></v-select>
          <v-text-field
            v-model="companyOrgUnit.taxIdentificationNumber"
            label="Tax Identification Number"
          ></v-text-field>
        </v-form>

        <h3 class="text-h5 mt-4 pt-4 mb-4 pb-4">Assign New {{ orgUnitTerminology }} to Users</h3>

        <v-data-table
          :items-per-page="-1"
          v-model="selectedUsers"
          show-select
          return-object
          :hide-default-footer="true"
          :must-sort="true"
          :no-data-text="''"
          :headers="headers"
          :items="users"
          class="ma-1"
          v-model:options="tableOptions"
          :loading="loading"
        >
        </v-data-table>
      </v-card-text>
      <v-card-text v-if="invitation && currentStep === 3 && process === 'existing'" class="wizard">
        <h4>Select Profile With Existing Connection</h4>
        <v-select
          v-model="selectedOrgUnit"
          :items="orgUnits"
          label="Profiles"
          item-title="name"
          item-value="id"
          :disabled="orgUnits.length == 1"
          required
        ></v-select>
      </v-card-text>
      <v-card-text v-if="invitation && currentStep === 4 && filteredParties.length > 1" class="wizard">
        <h4>Select Connection</h4>
        <v-select
          v-model="selectedParty"
          :items="filteredParties"
          label="Connection"
          item-title="displayName"
          item-value="id"
          required
        ></v-select>
      </v-card-text>
      <v-card-text v-if="invitation && currentStep === 4 && filteredParties.length === 0" class="wizard">
        <h2>Add new Connection</h2>
        <v-select
          v-model="newPartyType"
          :items="partyTypeList"
          label="Connection Type"
          item-title="name"
          item-value="id"
          required
        ></v-select>
        <v-text-field v-model="newPartyName" label="Name" required></v-text-field>
        <v-text-field v-model="newPartyCode" label="Number" required></v-text-field>
      </v-card-text>
      <v-card-actions v-if="invitation">
        <v-btn @click="prevStep" :disabled="currentStep === 1" size="large" color="primary" variant="flat"
          >Previous</v-btn
        >
        <v-btn @click="nextStep" :disabled="isNextButtonDisabled" size="large" color="primary" variant="flat"
          >Next</v-btn
        >
        <v-spacer />
        <v-btn @click="cancel" size="large" color="primary" variant="flat">Cancel</v-btn>
        <v-btn @click="validateAndSubmit" size="large" color="primary" variant="flat" :disabled="!canAccept"
          >Accept</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import PageHeader from "@/components/PageHeader.vue";
import servicesLookupService from "@/services/servicesLookupService";
import invitationService from "@/services/invitationService";
import companyOrgUnitService from "@/services/admin/orgUnitService";
import ValidationRules from "../../validationRules";
import userService from "@/services/userService";
import partyTypeList from "@/constants/partyTypeList";
import terminologyService from "@/services/terminologyService.js";
import terminology from "@/constants/terminology";

const ZIP_CODE = "Zip Code";
const POSTAL_CODE = "Postal Code";

export default {
  name: "Invitation",
  components: { PageHeader },
  props: {
    invitationId: Number,
    isAdditionalInvite: Boolean,
  },
  data() {
    return {
      invitation: null,
      tableOptions: {
        sortBy: [{ key: "emailAddress", order: "asc" }],
      },
      headers: [
        { title: "First Name", key: "firstName" },
        { title: "Last Name", key: "lastName" },
        { title: "Email", key: "emailAddress" },
      ],
      users: [],
      selectedUsers: [],
      loading: true,
      currentStep: 1,
      process: "existing",
      orgUnitTerminology: terminologyService.lookup(terminology.ORG_UNIT),
      companyOrgUnit: {
        name: null,
        code: null,
        addressLineOne: null,
        city: null,
        zipCode: null,
        country: null,
        state: null,
        taxIdentificationType: null,
        taxIdentificationNumber: null,
      },
      selectedCountry: null,
      selectedState: null,
      selectedTaxType: null,
      selectedOrgUnit: null,
      selectedParty: null,
      countries: [],
      states: [],
      taxTypes: [],
      orgUnits: [],
      parties: [],
      filteredParties: [],
      formValid: false,
      country: null,
      state: null,
      taxType: null,
      requiredRule: [v => !!v || "Required"],
      zipCodeRules: [],
      partyTypeList,
      newPartyType: null,
      newPartyName: null,
      newPartyCode: null,
    };
  },
  computed: {
    ...mapGetters(["selectedCompanyId"]),
    isNextButtonDisabled() {
      if (this.currentStep === 3) return true;
      return false;
    },
    canProceedToNextStep() {
      let canProceed = false;
      switch (this.currentStep) {
        case 1:
          canProceed = true;
          break;
        case 2:
          canProceed = true;
          break;
        case 3:
          if (this.isNewProcess) {
            canProceed = this.isOrgUnitValid;
          }
          if (this.isExistingProcess) {
            canProceed = !!this.selectedOrgUnit;
          }
          break;
        case 4:
          canProceed = !!this.selectedParty || this.canCreateNewParty;
          break;
      }

      return canProceed;
    },
    canAccept() {
      if (this.currentStep === 3) {
        if (this.process === "existing") {
          return !!this.selectedOrgUnit;
        } else {
          return true;
        }
      } else return false;
    },
    isOrgUnitValid() {
      return !!this.companyOrgUnit.name && !!this.companyOrgUnit.code;
    },
    isNewProcess() {
      return this.process === "new";
    },
    isExistingProcess() {
      return this.process === "existing";
    },
    zipCodeLabelName() {
      if (this.selectedCountry == null) return ZIP_CODE;

      return this.selectedCountry == "US" ? ZIP_CODE : POSTAL_CODE;
    },
    canCreateNewParty() {
      return this.newPartyType > 0 && !!this.newPartyCode;
    },
  },
  methods: {
    async init() {
      this.invitation = await invitationService.getInvitationById(this.invitationId);
      this.newPartyName = this.invitation.companyName;
      this.clearCompanyOrgUnit();
      this.currentStep = 1;
      this.process = "existing";
      this.locations = [];
      this.countries = [];
      this.taxTypes = [];
      this.states = [];
      this.selectedCountry = null;
      this.selectedState = null;
      this.selectedTaxType = null;
      this.parties = this.invitation.parties || [];
      this.selectedParty = null;
      this.country = null;
      this.state = null;
      this.taxType = null;

      this.partyIdsConnectedToSamePartyType = await invitationService.getPartyIdsOfConnectionsFromSamePartyType(
        this.invitation
      );

      var profileData = await companyOrgUnitService.getList();
      this.orgUnits = profileData.items;
      if (this.orgUnits.length == 1) {
        this.selectedOrgUnit = this.orgUnits[0].id;
      }

      this.users = (await userService.getUserList()).filter(u => u.isActive);

      this.populateOrgUnitNamesOfParties();

      this.locations = await servicesLookupService.getLocations();

      this.countries = Array.from(new Set(this.locations.map(item => JSON.stringify(item.country)))).map(jsonItem =>
        JSON.parse(jsonItem)
      );

      this.loading = false;
    },
    cancel() {
      this.$router.push({ name: "InvitationList" });
    },
    async nextStep() {
      if (this.currentStep === 3 && this.isNewProcess) {
        if (await this.isValidZipCode()) this.currentStep++;
      } else {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep === 4) {
        this.selectedParty = null;
      }

      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },
    validateAddress() {
      if (
        this.companyOrgUnit.country != null &&
        (this.companyOrgUnit.addressLineOne == null ||
          this.companyOrgUnit.city == null ||
          this.companyOrgUnit.zipCode == null ||
          this.companyOrgUnit.state == null)
      ) {
        return false;
      }
      return true;
    },
    async validateAndSubmit() {
      if (this.process === "new") {
        var { valid: isFormValid } = await this.$refs.form.validate();
        if (!isFormValid) {
          this.$root.$snackbar.error("There is some required data missing");
          return;
        }
        if (!this.validateAddress()) {
          this.$root.$snackbar.error("Please fill in all address fields when a country is selected");
          return;
        }
      }

      await this.acceptInvite();
    },

    onChangeProcess() {
      this.clearCompanyOrgUnit();
      this.selectedOrgUnit = null;
      this.selectedCountry = null;
      this.selectedState = null;
      this.selectedTaxType = null;

      if (this.isNewProcess)
        this.filteredParties = this.parties.filter(p => this.partyIdsConnectedToSamePartyType.includes(p.id));
      else this.filteredParties = [];
      this.selectedParty = this.filteredParties.length === 1 ? this.filteredParties[0].id : null;
    },
    async acceptInvite() {
      try {
        let orgUnit =
          this.process === "existing" ? this.orgUnits.find(x => x.id == this.selectedOrgUnit) : this.companyOrgUnit;

        orgUnit.users = this.selectedUsers;
        await invitationService.exchangeAccept(this.invitationId, orgUnit);
        const exchangeUser = await userService.getUserByExternalId(
          decodeURI(this.$auth0.user.value.sub),
          this.selectedCompanyId
        );
        this.$store.dispatch("documentTypeStore/updateDocumentTypes", { documentTypes: exchangeUser.documentTypes });
        this.$root.$snackbar.message("You are now connected to " + this.invitation.companyName);
        this.$store.dispatch("chatStore/updateRecipientParties");
        this.$store.dispatch("invitationStore/removePendingInvitation");
        this.$store.dispatch("invitationStore/removeAvailableConnectionsCount");
        this.$root.$emit("invitation.count.reload");
        this.$router.push({ name: "InvitationList" });
      } catch (err) {
        this.$root.$snackbar.error(err.response?.data?.error?.message || "Couldn't accept invitation");
      }
    },

    async updateLocations() {
      const location = this.selectedCountry
        ? this.locations.find(item => item.country.code === this.selectedCountry)
        : "US";

      if (location) {
        this.states = location.states || [];
        this.taxTypes = location.taxTypes || [];
      } else {
        this.states = [];
        this.taxTypes = [];
      }
    },
    updateZipCodeRule() {
      if (this.selectedCountry == "US") this.zipCodeRules = [ValidationRules.zipCodeRule];

      if (this.selectedCountry == "CA") this.zipCodeRules = [ValidationRules.postalCodeRule];
    },
    async isValidZipCode() {
      let isValid = true;

      if (this.companyOrgUnit.zipCode != "" && this.selectedCountry == null) {
        isValid = false;
        this.$root.$snackbar.error("A country must be selected when the Zip Code is populated");
      } else if (!(await this.$refs.form.validate()).valid) {
        isValid = false;
        let zipCodeName = this.selectedCountry == "US" ? ZIP_CODE : POSTAL_CODE;
        this.$root.$snackbar.error("Please fix the " + zipCodeName + " issue");
      }

      return isValid;
    },
    async onCountryChange(selected) {
      this.selectedCountry = selected;
      this.selectedState = null;
      this.selectedTaxType = null;
      this.companyOrgUnit.zipCode = null;

      this.updateZipCodeRule();
      await this.updateLocations();
    },
    clearCompanyOrgUnit() {
      this.companyOrgUnit = {
        name: null,
        code: null,
        addressLineOne: null,
        city: null,
        zipCode: null,
        country: null,
        state: null,
        taxIdentificationType: null,
        taxIdentificationNumber: null,
      };
    },
    populateOrgUnitNamesOfParties() {
      if (this.parties.length > 1) {
        this.parties.forEach(p => {
          let names = [];
          p.companyOrgUnitIds.forEach(id => {
            let orgUnit = this.orgUnits.find(x => x.id == id);
            names.push(orgUnit.name);
          });
          p.orgUnitNames = names.join(", ");
          p.displayName = p.name + " (" + p.orgUnitNames + ")";
        });
      }
    },
  },
  async mounted() {
    await this.init();
  },
};
</script>

<style scoped>
.wizard {
  height: 100%;
}
</style>
